import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../utils/cn';
import { Button } from '../ui/Button';

export const FileUpload = ({ onFileUpload, isUploading, currentFile, onClearFile }) => {
    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles?.length > 0) {
            onFileUpload(acceptedFiles[0]);
        }
    }, [onFileUpload]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
        },
        maxFiles: 1,
        disabled: isUploading || !!currentFile,
    });

    return (
        <div className="w-full">
            <AnimatePresence mode="wait">
                {!currentFile ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        {...getRootProps()}
                        className={cn(
                            'relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 cursor-pointer',
                            isDragActive
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-slate-200 hover:border-primary-400 hover:bg-slate-50',
                            isUploading && 'opacity-50 cursor-not-allowed'
                        )}
                    >
                        <input {...getInputProps()} />
                        <div className="flex flex-col items-center gap-4">
                            <div className={cn(
                                'h-12 w-12 rounded-full flex items-center justify-center transition-colors',
                                isDragActive ? 'bg-primary-100 text-primary-600' : 'bg-slate-100 text-slate-500'
                            )}>
                                {isUploading ? (
                                    <Loader2 className="h-6 w-6 animate-spin" />
                                ) : (
                                    <Upload className="h-6 w-6" />
                                )}
                            </div>
                            <div>
                                <p className="text-lg font-semibold text-slate-900">
                                    {isUploading ? 'Uploading...' : 'Upload Contract'}
                                </p>
                                <p className="text-sm text-slate-500 mt-1">
                                    Drag & drop your PDF here, or click to browse
                                </p>
                            </div>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between shadow-sm"
                    >
                        <div className="flex items-center gap-4">
                            <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                <FileText className="h-5 w-5 text-primary-600" />
                            </div>
                            <div>
                                <p className="font-medium text-slate-900 truncate max-w-[200px]">
                                    {currentFile.name}
                                </p>
                                <p className="text-xs text-slate-500">
                                    {(currentFile.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClearFile();
                            }}
                            className="text-slate-400 hover:text-danger-600 hover:bg-danger-50"
                        >
                            <X className="h-5 w-5" />
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
